import { useEffect, useMemo, useState, useCallback } from 'react';
import { adminHeaders } from '../../lib/adminAuth';
import './CleaningPage.css';
import CleanerManager from './cleaning/CleanerManager';
import { weekdaysMon } from '../../lib/adminI18n';
import CleaningDashboard from './cleaning/CleaningDashboard';
import CleaningTaskList, { ListPreset } from './cleaning/CleaningTaskList';
import ChecklistManager from './cleaning/ChecklistManager';

import { tx } from '../../lib/adminI18n';
// ------------------------------------------------------------
// 管理画面: 清掃スケジュール  /admin/cleaning
//   設計: .plan/admin-calendar-cleaning-design-v2.md（v3.4）
//   目的: 今月・来月の清掃予定を一目で把握し、清掃完了を記録する。
//   清掃日＝予約のチェックアウト日（自社・OTAとも reservations から自動生成）。
//   手動追加(source='manual')は予約と無関係に1件足せる。状態は 要清掃/完了（+取消）。
// ------------------------------------------------------------

interface CleaningTask {
  id: number;
  property_id: string;
  property_name: string | null;
  booking_prefix: string | null;
  abbreviation: string | null;
  cleaning_date: string;      // JST YYYY-MM-DD
  checkin_date: string | null;
  status: 'pending' | 'in_progress' | 'completed' | 'needs_review' | 'cancelled';
  issue_reason?: string | null;
  actual_start_at?: string | null;
  actual_end_at?: string | null;
  planned_start_at?: string | null;
  planned_end_at?: string | null;
  assignees?: { id: number; name: string; is_lead: number; is_active: number }[];
  source: 'direct' | 'ota' | 'manual';
  reservation_id: string | null;
  note: string | null;
}
interface PropertyOpt { id: string; name: string; }

const SOURCE_LABEL: Record<string, string> = { get direct() { return tx('clnP.0'); }, ota: 'OTA', get manual() { return tx('clnP.1'); } };

// ステータス表示（手順3）
const STATUS_LABEL: Record<string, string> = {
  get pending() { return tx('clnP.2'); }, get in_progress() { return tx('clnP.3'); }, get completed() { return tx('clnP.4'); }, get needs_review() { return tx('clnP.5'); }, get cancelled() { return tx('clnP.6'); },
};
const STATUS_ICON: Record<string, string> = { pending: '🧹', in_progress: '🔄', completed: '✅', needs_review: '⚠️', cancelled: '' };

// UTC ISO → JST の 'MM/DD HH:MM'
function jstTime(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}

// JST(UTC+9) の「今日」を YYYY-MM-DD で得る（日本はDSTなし）。
function jstTodayStr(): string {
  return new Date(Date.now() + 9 * 60 * 60 * 1000).toISOString().slice(0, 10);
}
function ymd(y: number, m0: number, d: number): string {
  return `${y}-${String(m0 + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}
function monthRange(y: number, m0: number): { from: string; to: string } {
  const last = new Date(y, m0 + 1, 0).getDate();
  return { from: ymd(y, m0, 1), to: ymd(y, m0, last) };
}

export default function CleaningPage() {
  const today = jstTodayStr();
  const now = new Date(today + 'T00:00:00');
  const [year, setYear] = useState(now.getFullYear());
  const [month0, setMonth0] = useState(now.getMonth()); // 0-based
  const [tasks, setTasks] = useState<CleaningTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  type Tab = 'dashboard' | 'calendar' | 'open' | 'done' | 'cleaners' | 'checklist';
  const [tab, setTab] = useState<Tab>('dashboard');
  const [listPreset, setListPreset] = useState<ListPreset | undefined>(undefined);
  // 一覧・ダッシュボードの行から、カレンダーのその日を開く
  function openDate(ymd: string) {
    setYear(Number(ymd.slice(0, 4)));
    setMonth0(Number(ymd.slice(5, 7)) - 1);
    setSelectedDate(ymd);
    setTab('calendar');
  }
  function openUnassigned() {
    setListPreset({ cleaner: 'unassigned', period: 'this_week' });
    setTab('open');
  }
  // 担当者の変更（手順4）
  const [cleanerOpts, setCleanerOpts] = useState<{ id: number; name: string; is_active: number; property_ids: string[] }[]>([]);
  const [assignTask, setAssignTask] = useState<CleaningTask | null>(null);
  const [assignSel, setAssignSel] = useState<number[]>([]);
  const [assignLead, setAssignLead] = useState<number | null>(null);

  // 手動追加フォーム
  const [properties, setProperties] = useState<PropertyOpt[]>([]);
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ property_id: '', cleaning_date: '', note: '', planned_start: '', planned_end: '' });
  // 予定時刻・メモの編集
  const [editTask, setEditTask] = useState<CleaningTask | null>(null);
  const [editForm, setEditForm] = useState({ planned_start: '', planned_end: '', note: '' });
  const [editErr, setEditErr] = useState('');
  // 物件の既定時刻（チェックアウト〜チェックイン）。予定時刻の初期値に使う
  const [propTimes, setPropTimes] = useState<Record<string, { out: string; in: string }>>({});

  const load = useCallback(async (y: number, m0: number) => {
    setLoading(true); setMsg('');
    const { from, to } = monthRange(y, m0);
    try {
      const res = await fetch(`/api/admin/cleaning?from=${from}&to=${to}`, { headers: adminHeaders(), credentials: 'include' });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { setMsg(tx('clnP.7')); setTasks([]); return; }
      setTasks(d.tasks || []);
    } finally { setLoading(false); }
  }, []);

  const loadProps = useCallback(async () => {
    const res = await fetch('/api/admin/properties', { headers: adminHeaders(), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    const list = (Array.isArray(d) ? d : d.properties || []) as any[];
    setProperties(list.map((p) => ({ id: p.id, name: p.name })));
    const tm: Record<string, { out: string; in: string }> = {};
    const hhmm = (v: unknown) => { const m = /^(\d{1,2}):(\d{2})/.exec(String(v || '')); return m ? `${m[1].padStart(2, '0')}:${m[2]}` : ''; };
    for (const p of list) tm[p.id] = { out: hhmm(p.checkout_time), in: hhmm(p.checkin_time) };
    setPropTimes(tm);
  }, []);

  useEffect(() => { load(year, month0); }, [year, month0, load]);
  useEffect(() => { loadProps(); }, [loadProps]);

  const loadCleaners = useCallback(async () => {
    const res = await fetch('/api/admin/cleaners', { headers: adminHeaders(), credentials: 'include' });
    const d = await res.json().catch(() => ({}));
    setCleanerOpts(((d.cleaners || []) as any[]).map((c) => ({
      id: c.id, name: c.name, is_active: c.is_active, property_ids: (c.properties || []).map((p: any) => p.property_id),
    })));
  }, []);
  useEffect(() => { loadCleaners(); }, [loadCleaners]);

  function openAssign(t: CleaningTask) {
    const cur = t.assignees || [];
    setAssignTask(t);
    setAssignSel(cur.map((a) => a.id));
    setAssignLead(cur.find((a) => a.is_lead)?.id ?? null);
    setMsg('');
  }
  async function saveAssign(e: React.FormEvent) {
    e.preventDefault();
    if (!assignTask) return;
    setBusy(true);
    try {
      const res = await fetch(`/api/admin/cleaning/${assignTask.id}/assignees`, {
        method: 'PUT', headers: adminHeaders({ 'Content-Type': 'application/json' }), credentials: 'include',
        body: JSON.stringify({ cleaner_ids: assignSel, lead_id: assignLead }),
      });
      const d = await res.json().catch(() => ({} as any));
      if (!res.ok) { setMsg(d?.error || tx('clnP.8')); return; }
      setAssignTask(null);
      await load(year, month0);
    } finally { setBusy(false); }
  }

  function prevMonth() { setSelectedDate(null); setMonth0((m) => { if (m === 0) { setYear((y) => y - 1); return 11; } return m - 1; }); }
  function nextMonth() { setSelectedDate(null); setMonth0((m) => { if (m === 11) { setYear((y) => y + 1); return 0; } return m + 1; }); }

  // 日付 -> その日の有効タスク（取消以外）
  const byDate = useMemo(() => {
    const map: Record<string, CleaningTask[]> = {};
    for (const t of tasks) {
      if (t.status === 'cancelled') continue;
      (map[t.cleaning_date] ||= []).push(t);
    }
    return map;
  }, [tasks]);

  // 日付 -> その日にチェックインする予約（清掃日とは別枠でカレンダーに出す）。
  // 清掃タスクは checkin_date を保持しているので、それをチェックイン日で集約する。
  const checkinByDate = useMemo(() => {
    const map: Record<string, CleaningTask[]> = {};
    for (const t of tasks) {
      if (t.status === 'cancelled') continue;
      if (!t.checkin_date) continue;
      (map[t.checkin_date] ||= []).push(t);
    }
    return map;
  }, [tasks]);

  const counts = useMemo(() => {
    let pending = 0, inProgress = 0, review = 0, completed = 0;
    for (const t of tasks) {
      if (t.status === 'pending') pending++;
      else if (t.status === 'in_progress') inProgress++;
      else if (t.status === 'needs_review') review++;
      else if (t.status === 'completed') completed++;
    }
    return { pending, inProgress, review, completed };
  }, [tasks]);

  // カレンダーのセル（月曜始まり）。前後月の余白も敷く。
  const cells = useMemo(() => {
    const first = new Date(year, month0, 1);
    const firstDow = (first.getDay() + 6) % 7; // Mon=0
    const daysInMonth = new Date(year, month0 + 1, 0).getDate();
    const arr: { date: string | null; day: number | null }[] = [];
    for (let i = 0; i < firstDow; i++) arr.push({ date: null, day: null });
    for (let d = 1; d <= daysInMonth; d++) arr.push({ date: ymd(year, month0, d), day: d });
    while (arr.length % 7 !== 0) arr.push({ date: null, day: null });
    return arr;
  }, [year, month0]);

  async function act(id: number, path: string) {
    setBusy(true); setMsg('');
    try {
      const res = await fetch(`/api/admin/cleaning/${id}/${path}`, { method: 'PATCH', headers: adminHeaders(), credentials: 'include' });
      if (!res.ok) { setMsg(tx('clnP.9')); return; }
      await load(year, month0);
    } finally { setBusy(false); }
  }

  // ステータス変更（要確認は理由を入力）
  async function changeStatus(id: number, status: string) {
    let reason: string | undefined;
    if (status === 'needs_review') {
      const r = window.prompt(tx('clnP.10'));
      if (r === null) return;
      if (!r.trim()) { setMsg(tx('clnP.11')); return; }
      reason = r.trim();
    }
    setBusy(true); setMsg('');
    try {
      const res = await fetch(`/api/admin/cleaning/${id}/status`, {
        method: 'PATCH', headers: adminHeaders({ 'Content-Type': 'application/json' }), credentials: 'include',
        body: JSON.stringify({ status, reason }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({} as any));
        setMsg(d?.error || tx('clnP.9')); return;
      }
      await load(year, month0);
    } finally { setBusy(false); }
  }

  function openEdit(t: CleaningTask) {
    const def = propTimes[t.property_id] || { out: '', in: '' };
    setEditErr('');
    setEditTask(t);
    setEditForm({
      planned_start: t.planned_start_at ? t.planned_start_at.slice(11, 16) : '',
      planned_end: t.planned_end_at ? t.planned_end_at.slice(11, 16) : '',
      note: t.note || '',
    });
    // 未設定なら物件のチェックアウト〜チェックイン時刻を候補として入れる（保存するまで反映されない）
    if (!t.planned_start_at && !t.planned_end_at && (def.out || def.in)) {
      setEditForm({ planned_start: def.out, planned_end: def.in, note: t.note || '' });
    }
  }
  async function saveEdit(e: React.FormEvent) {
    e.preventDefault();
    if (!editTask) return;
    setEditErr('');
    if (editForm.planned_start && editForm.planned_end && editForm.planned_end <= editForm.planned_start) {
      setEditErr(tx('clnP.12')); return;
    }
    setBusy(true);
    try {
      const res = await fetch(`/api/admin/cleaning/${editTask.id}`, {
        method: 'PATCH', headers: adminHeaders({ 'Content-Type': 'application/json' }), credentials: 'include',
        body: JSON.stringify(editForm),
      });
      const d = await res.json().catch(() => ({} as any));
      if (!res.ok) { setEditErr(d?.error || tx('clnP.13')); return; }
      setEditTask(null);
      await load(year, month0);
    } finally { setBusy(false); }
  }

  async function submitAdd(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setMsg('');
    try {
      const res = await fetch('/api/admin/cleaning/manual', {
        method: 'POST',
        headers: adminHeaders({ 'Content-Type': 'application/json' }),
        credentials: 'include',
        body: JSON.stringify(addForm),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { setMsg(d.error || tx('clnP.14')); return; }
      setShowAdd(false);
      setAddForm({ property_id: '', cleaning_date: '', note: '', planned_start: '', planned_end: '' });
      await load(year, month0);
    } finally { setBusy(false); }
  }

  // カレンダー表示用ラベル: 略語(abbreviation) → 物件コード(booking_prefix: SD, FT) → 物件名の先頭2文字
  const code = (t: CleaningTask) =>
    (t.abbreviation && t.abbreviation.trim())
      || t.booking_prefix
      || (t.property_name ? t.property_name.slice(0, 2) : '—');
  const WEEKDAYS = weekdaysMon();
  const selectedTasks = selectedDate ? (byDate[selectedDate] || []) : [];
  const selectedCheckins = selectedDate ? (checkinByDate[selectedDate] || []) : [];

  return (
    <div className="cleaning-page">
      <div className="cln-head">
        <h1>{tx('clnP.15')}</h1>
        {tab === 'calendar' && (
          <button className="cln-btn cln-btn-primary" onClick={() => { setShowAdd(true); setAddForm({ property_id: '', cleaning_date: selectedDate || '', note: '', planned_start: '', planned_end: '' }); }}>
            {tx('clnP.16')}
          </button>
        )}
      </div>

      <div className="cln-tabs" role="tablist">
        {([
          ['dashboard', tx('clnP.17')],
          ['calendar', tx('clnP.18')],
          ['open', tx('clnP.19')],
          ['done', tx('clnP.20')],
          ['cleaners', tx('clnP.21')],
          ['checklist', tx('clnCk.tab')],
        ] as [Tab, string][]).map(([k, label]) => (
          <button key={k} role="tab" aria-selected={tab === k} className={`cln-tab ${tab === k ? 'active' : ''}`}
            onClick={() => { if (k !== 'open') setListPreset(undefined); setTab(k); }}>{label}</button>
        ))}
      </div>

      {tab === 'checklist' ? (
        <ChecklistManager properties={properties} />
      ) : tab === 'cleaners' ? (
        <CleanerManager properties={properties} onChanged={() => { loadCleaners(); load(year, month0); }} />
      ) : tab === 'dashboard' ? (
        <CleaningDashboard onOpenDate={openDate} onOpenUnassigned={openUnassigned} />
      ) : tab === 'open' || tab === 'done' ? (
        <CleaningTaskList key={`${tab}-${listPreset?.cleaner || ''}`} mode={tab} properties={properties} cleaners={cleanerOpts}
          onOpenDate={openDate} preset={tab === 'open' ? listPreset : undefined} />
      ) : (<>

      <div className="cln-summary">
        <span className="cln-chip cln-chip-pending">{tx('clnP.2')} {counts.pending}</span>
        <span className="cln-chip cln-chip-progress">{tx('clnP.3')} {counts.inProgress}</span>
        <span className="cln-chip cln-chip-review">{tx('clnP.5')} {counts.review}</span>
        <span className="cln-chip cln-chip-done">{tx('clnP.4')} {counts.completed}</span>
        <span className="cln-chip cln-chip-checkin">{tx('clnP.22')} {Object.values(checkinByDate).reduce((n, a) => n + a.length, 0)}</span>
      </div>

      <div className="cln-toolbar">
        <button className="cln-nav" onClick={prevMonth} aria-label={tx('clnP.23')}>{tx('clnP.24')}</button>
        <span className="cln-month">{year}{tx('clnP.25')}{month0 + 1}{tx('clnP.26')}</span>
        <button className="cln-nav" onClick={nextMonth} aria-label={tx('clnP.27')}>{tx('clnP.28')}</button>
      </div>

      {msg && <p className="cln-msg">{msg}</p>}

      {loading ? <p>{tx('clnP.29')}</p> : (
        <div className="cln-grid">
          {WEEKDAYS.map((w) => <div key={w} className="cln-dow">{w}</div>)}
          {cells.map((cell, i) => {
            if (!cell.date) return <div key={i} className="cln-cell cln-empty" />;
            const list = byDate[cell.date] || [];
            const checkins = checkinByDate[cell.date] || [];
            const isToday = cell.date === today;
            const isSel = cell.date === selectedDate;
            return (
              <div key={i}
                className={`cln-cell${isToday ? ' today' : ''}${isSel ? ' selected' : ''}`}
                onClick={() => setSelectedDate(cell.date)}>
                <div className="cln-daynum">{cell.day}</div>
                <div className="cln-chips">
                  {list.map((t) => (
                    <div key={t.id} className={`cln-task ${t.status}`}
                      title={tx('clnP.30', { p0: t.property_name || '', p1: SOURCE_LABEL[t.source], p2: t.cleaning_date })}>
                      {STATUS_ICON[t.status] || '🧹'} {code(t)}
                    </div>
                  ))}
                  {checkins.map((t) => (
                    <div key={`in-${t.id}`} className="cln-task cln-checkin"
                      title={tx('clnP.31', { p0: t.property_name || '', p1: SOURCE_LABEL[t.source], p2: t.checkin_date })}>
                      🛎️ {code(t)}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* 日付別詳細 */}
      {selectedDate && (
        <div className="cln-detail">
          <h2>{selectedDate} {tx('clnP.32')}</h2>
          {selectedTasks.length === 0 ? (
            <p className="cln-detail-empty">{tx('clnP.33')}</p>
          ) : selectedTasks.map((t) => (
            <div key={t.id} className={`cln-detail-row ${t.status}`}>
              <div className="cln-detail-main">
                <span className="cln-detail-name">{t.property_name || t.property_id}{tx('clnP.34')}{code(t)}{tx('clnP.35')}</span>
                <span className={`cln-src cln-src-${t.source}`}>{SOURCE_LABEL[t.source]}</span>
                <span className={`cln-status cln-status-${t.status}`}>{STATUS_LABEL[t.status] || t.status}</span>
              </div>
              <div className="cln-detail-meta">
                {tx('clnP.36')} <strong>{t.cleaning_date}</strong>
                <div className="cln-planned">
                  {tx('clnP.37')} {t.planned_start_at || t.planned_end_at
                    ? <strong>{t.planned_start_at?.slice(11, 16) || '—'} 〜 {t.planned_end_at?.slice(11, 16) || '—'}</strong>
                    : <span className="cln-planned-none">{tx('clnP.39')}</span>}
                </div>
                {t.checkin_date ? <> {tx('clnP.40')} {t.checkin_date}</> : null}
                {(t.actual_start_at || t.actual_end_at) ? (
                  <div className="cln-actual">{tx('clnP.41')} {jstTime(t.actual_start_at) || '—'} 〜 {jstTime(t.actual_end_at) || '—'}</div>
                ) : null}
                {t.status === 'needs_review' && t.issue_reason ? (
                  <div className="cln-issue">{tx('clnP.42')}<br />{t.issue_reason.split('\n').map((l, i) => <span key={i}>{l}<br /></span>)}</div>
                ) : null}
                {t.note ? <div className="cln-note">{tx('clnP.43')} {t.note}</div> : null}
                <div className="cln-assignees">
                  {tx('clnP.44')} {(t.assignees || []).length
                    ? (t.assignees || []).map((a) => (
                        <span key={a.id} className={`cln-assignee ${a.is_active ? '' : 'inactive'}`}>
                          {a.is_lead ? '★' : ''}{a.name}{a.is_active ? '' : tx('clnP.45')}
                        </span>))
                    : <span className="cln-unassigned">{tx('clnP.46')}</span>}
                </div>
              </div>
              <div className="cln-detail-actions">
                {t.status === 'pending' && <button className="cln-btn cln-btn-ghost" disabled={busy} onClick={() => changeStatus(t.id, 'in_progress')}>{tx('clnP.47')}</button>}
                {t.status !== 'completed' && <button className="cln-btn cln-btn-primary" disabled={busy} onClick={() => changeStatus(t.id, 'completed')}>{tx('clnP.48')}</button>}
                {(t.status === 'pending' || t.status === 'in_progress') && <button className="cln-btn cln-btn-ghost" disabled={busy} onClick={() => changeStatus(t.id, 'needs_review')}>{tx('clnP.49')}</button>}
                {t.status !== 'pending' && <button className="cln-btn cln-btn-ghost" disabled={busy} onClick={() => changeStatus(t.id, 'pending')}>{tx('clnP.50')}</button>}
                <button className="cln-btn cln-btn-ghost" disabled={busy} onClick={() => openAssign(t)}>{tx('clnP.51')}</button>
                <button className="cln-btn cln-btn-ghost" disabled={busy} onClick={() => openEdit(t)}>{tx('clnP.52')}</button>
                <button className="cln-btn cln-btn-ghost" disabled={busy} onClick={() => { if (confirm(tx('clnP.53'))) act(t.id, 'cancel'); }}>{tx('clnP.6')}</button>
                {t.reservation_id && (
                  <a className="cln-link" href={`/admin/reservations?highlight=${t.reservation_id}`}>{tx('clnP.54')}</a>
                )}
              </div>
            </div>
          ))}
          {selectedCheckins.length > 0 && (
            <div className="cln-checkin-list">
              <h3 className="cln-checkin-head">{tx('clnP.55')}{selectedCheckins.length}{tx('clnP.56')}</h3>
              {selectedCheckins.map((t) => (
                <div key={`cin-${t.id}`} className="cln-checkin-row">
                  <span className="cln-detail-name">{t.property_name || t.property_id}{tx('clnP.34')}{code(t)}{tx('clnP.35')}</span>
                  <span className={`cln-src cln-src-${t.source}`}>{SOURCE_LABEL[t.source]}</span>
                  <span className="cln-checkin-note">{tx('clnP.57')} {t.cleaning_date}</span>
                  {t.reservation_id && (
                    <a className="cln-link" href={`/admin/reservations?highlight=${t.reservation_id}`}>{tx('clnP.54')}</a>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 手動追加モーダル */}
      </>)}

      {assignTask && (
        <div className="cln-modal-overlay" onClick={() => !busy && setAssignTask(null)}>
          <form className="cln-modal" onClick={(e) => e.stopPropagation()} onSubmit={saveAssign}>
            <h3>{tx('clnP.58')}</h3>
            <p className="cln-assign-sub">{assignTask.property_name || assignTask.property_id} {tx('clnP.59')} {assignTask.cleaning_date}</p>
            {cleanerOpts.filter((c) => c.is_active || assignSel.includes(c.id)).length === 0 ? (
              <p className="cln-detail-empty">{tx('clnP.60')}</p>
            ) : (
              <div className="cln-assign-list">
                {[...cleanerOpts]
                  .filter((c) => c.is_active || assignSel.includes(c.id))
                  .sort((a, b) => Number(b.property_ids.includes(assignTask.property_id)) - Number(a.property_ids.includes(assignTask.property_id)))
                  .map((c) => {
                    const on = assignSel.includes(c.id);
                    return (
                      <div key={c.id} className="cln-assign-row">
                        <label>
                          <input type="checkbox" checked={on}
                            onChange={() => {
                              setAssignSel((cur) => on ? cur.filter((x) => x !== c.id) : [...cur, c.id]);
                              if (on && assignLead === c.id) setAssignLead(null);
                            }} /> {c.name}
                          {c.property_ids.includes(assignTask.property_id) && <span className="cln-assign-tag">{tx('clnP.61')}</span>}
                          {!c.is_active && <span className="cln-assign-tag off">{tx('clnP.62')}</span>}
                        </label>
                        {on && (
                          <label className="cln-assign-lead">
                            <input type="radio" name="lead" checked={assignLead === c.id} onChange={() => setAssignLead(c.id)} /> 代表者
                          </label>
                        )}
                      </div>
                    );
                  })}
              </div>
            )}
            <p className="clm-note">{tx('clnP.63')}</p>
            <div className="cln-modal-actions">
              <button type="button" className="cln-btn cln-btn-ghost" disabled={busy} onClick={() => setAssignTask(null)}>{tx('clnP.64')}</button>
              <button type="submit" className="cln-btn cln-btn-primary" disabled={busy}>{tx('clnP.65')}</button>
            </div>
          </form>
        </div>
      )}

      {editTask && (
        <div className="cln-modal-overlay" onClick={() => !busy && setEditTask(null)}>
          <form className="cln-modal" onClick={(e) => e.stopPropagation()} onSubmit={saveEdit}>
            <h2>{tx('clnP.52')}</h2>
            <p className="cln-modal-sub">{editTask.property_name || editTask.property_id} {tx('clnP.59')} {editTask.cleaning_date}</p>
            <div className="cln-time-row">
              <label className="cln-label">{tx('clnP.66')}
                <input className="cln-input" type="time" step={300} value={editForm.planned_start} onChange={(e) => setEditForm({ ...editForm, planned_start: e.target.value })} />
              </label>
              <label className="cln-label">{tx('clnP.67')}
                <input className="cln-input" type="time" step={300} value={editForm.planned_end} onChange={(e) => setEditForm({ ...editForm, planned_end: e.target.value })} />
              </label>
            </div>
            <div className="cln-time-tools">
              {propTimes[editTask.property_id] && (propTimes[editTask.property_id].out || propTimes[editTask.property_id].in) && (
                <button type="button" className="cln-link-btn" onClick={() => setEditForm({ ...editForm, planned_start: propTimes[editTask.property_id].out, planned_end: propTimes[editTask.property_id].in })}>
                  {tx('clnP.68')}{propTimes[editTask.property_id].out || '—'}〜{propTimes[editTask.property_id].in || '—'}{tx('clnP.35')}
                </button>
              )}
              <button type="button" className="cln-link-btn" onClick={() => setEditForm({ ...editForm, planned_start: '', planned_end: '' })}>{tx('clnP.69')}</button>
            </div>
            <label className="cln-label">{tx('clnP.70')}
              <textarea className="cln-input" rows={3} value={editForm.note} onChange={(e) => setEditForm({ ...editForm, note: e.target.value })} />
            </label>
            {editErr && <p className="cln-msg cln-msg-error">⚠ {editErr}</p>}
            <div className="cln-modal-actions">
              <button type="button" className="cln-btn cln-btn-ghost" disabled={busy} onClick={() => setEditTask(null)}>{tx('clnP.64')}</button>
              <button type="submit" className="cln-btn cln-btn-primary" disabled={busy}>{busy ? tx('clnP.71') : tx('clnP.65')}</button>
            </div>
          </form>
        </div>
      )}

      {showAdd && (
        <div className="cln-modal-overlay" onClick={() => !busy && setShowAdd(false)}>
          <form className="cln-modal" onClick={(e) => e.stopPropagation()} onSubmit={submitAdd}>
            <h2>{tx('clnP.72')}</h2>
            <p className="cln-modal-sub">{tx('clnP.73')}</p>
            <label className="cln-label">{tx('clnP.74')}
              <select className="cln-input" required value={addForm.property_id} onChange={(e) => setAddForm({ ...addForm, property_id: e.target.value })}>
                <option value="">{tx('clnP.75')}</option>
                {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </label>
            <label className="cln-label">{tx('clnP.76')}
              <input className="cln-input" type="date" required value={addForm.cleaning_date} onChange={(e) => setAddForm({ ...addForm, cleaning_date: e.target.value })} />
            </label>
            <div className="cln-time-row">
              <label className="cln-label">{tx('clnP.77')}
                <input className="cln-input" type="time" step={300} value={addForm.planned_start} onChange={(e) => setAddForm({ ...addForm, planned_start: e.target.value })} />
              </label>
              <label className="cln-label">{tx('clnP.78')}
                <input className="cln-input" type="time" step={300} value={addForm.planned_end} onChange={(e) => setAddForm({ ...addForm, planned_end: e.target.value })} />
              </label>
            </div>
            {addForm.property_id && propTimes[addForm.property_id] && (propTimes[addForm.property_id].out || propTimes[addForm.property_id].in) && (
              <button type="button" className="cln-link-btn" onClick={() => setAddForm({ ...addForm, planned_start: propTimes[addForm.property_id].out, planned_end: propTimes[addForm.property_id].in })}>
                {tx('clnP.68')}{propTimes[addForm.property_id].out || '—'}〜{propTimes[addForm.property_id].in || '—'}{tx('clnP.35')}
              </button>
            )}
            <label className="cln-label">{tx('clnP.79')}
              <input className="cln-input" value={addForm.note} onChange={(e) => setAddForm({ ...addForm, note: e.target.value })} />
            </label>
            {msg && <p className="cln-msg">{msg}</p>}
            <div className="cln-modal-actions">
              <button type="button" className="cln-btn cln-btn-ghost" disabled={busy} onClick={() => setShowAdd(false)}>{tx('clnP.64')}</button>
              <button type="submit" className="cln-btn cln-btn-primary" disabled={busy}>{tx('clnP.80')}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
